<template>
	<section v-if="roomsData" class="overview-room-table">
		<!-- Compact Header with Filter/Search -->
		<div class="table-controls">
			<div class="view-toggle">
				<button 
					:class="{ active: viewMode === 'table' }" 
					@click="viewMode = 'table'"
					class="toggle-btn"
				>
					<span class="icon">⊞</span> {{ $t('table') }}
				</button>
				<button 
					:class="{ active: viewMode === 'cards' }" 
					@click="viewMode = 'cards'"
					class="toggle-btn"
				>
					<span class="icon">⊡</span> {{ $t('cards') }}
				</button>
			</div>
			<div class="quick-stats">
				<span class="stat-item">
					<span class="stat-value">{{ roomsData.length }}</span>
					<span class="stat-label">{{ $t('rooms') }}</span>
				</span>
			</div>
		</div>

		<!-- Card View -->
		<div v-if="viewMode === 'cards'" class="cards-container">
			<div 
				v-for="(room, index) in roomsData" 
				:key="room.name + index"
				class="room-card"
				:class="getCardStatusClass(room)"
			>
				<div class="card-header">
					<h3 class="room-title">{{ room.name }}</h3>
					<div v-if="!inSurveyOverviewPage" class="status-indicator">
						<img 
							:src="iconToShow(room.openTime, room.isOpen)" 
							class="status-icon-small"
							:alt="getStatusText(room.openTime, room.isOpen)"
						/>
					</div>
				</div>
				
				<div class="card-content">
					<div v-if="!inSurveyOverviewPage && room.arrival && room.arrival.time !== ''" class="metric">
						<span class="metric-label">{{ $t('last_arrival') }}</span>
						<span class="metric-value">
							{{ formatArrivalTime(room.arrival.time) }}
							<span class="arrival-badge">{{ room.arrival.count }}</span>
						</span>
					</div>
					
					<div v-if="!inSurveyOverviewPage" class="metric">
						<span class="metric-label">{{ $t('score') }}</span>
						<div class="score-display" :style="room.score | statusColor">
							{{ Math.round(room.score) }}
							<span :class="['trend', room.trend]">
								{{ room.trend === 'up' ? '📈' : '📉' }}
							</span>
						</div>
					</div>

					<div v-if="inSurveyOverviewPage" class="metric">
						<span class="metric-label">{{ $t('clicks') }}</span>
						<span class="metric-value large">{{ room.count }}</span>
					</div>
				</div>
			</div>
		</div>

		<!-- Table View with Modern Design -->
		<div v-else class="modern-table">
			<div class="table-header-row">
				<div
					v-for="header in inSurveyOverviewPage ? surveyOverviewTableHeader : overviewTableHeader"
					:key="header"
					@click="sortTable(header)"
					:class="{ 'sorted': sortKey === header }"
					class="header-cell"
				>
					<div class="header-content">
						<span>{{ $t(header) }}</span>
						<div class="sort-controls">
							<button 
								:class="{ active: sortKey === header && sortOrder === 1 }"
								class="sort-btn up"
							>▲</button>
							<button 
								:class="{ active: sortKey === header && sortOrder === -1 }"
								class="sort-btn down"
							>▼</button>
						</div>
					</div>
				</div>
			</div>

			<div class="table-body-modern">
				<div 
					v-for="(room, index) in roomsData" 
					:key="room.name + index"
					class="table-row-modern"
					:class="{ 'urgent': isUrgent(room) }"
				>
					<div class="cell name-cell">
						<div class="room-info">
							<span class="room-name">{{ room.name }}</span>
						</div>
					</div>

					<div v-if="!inSurveyOverviewPage" class="cell arrival-cell">
						<div v-if="room.arrival && room.arrival.time !== ''" class="arrival-info">
							<div class="time-display">{{ formatArrivalTime(room.arrival.time) }}</div>
							<div class="count-pill">{{ room.arrival.count }}</div>
						</div>
						<div v-else class="no-arrival">{{ $t('no_recent_arrivals') }}</div>
					</div>

					<div v-if="!inSurveyOverviewPage" class="cell status-cell">
						<div class="status-icon-container">
							<img 
								:src="iconToShow(room.openTime, room.isOpen)" 
								class="status-icon"
								:alt="getStatusText(room.openTime, room.isOpen)"
							/>
						</div>
					</div>

					<div v-if="!inSurveyOverviewPage" class="cell score-cell">
						<div class="score-widget" :style="room.score | statusColor">
							<div class="score-main">{{ Math.round(room.score) }}</div>
							<div class="score-trend" :class="room.trend">
								{{ room.trend === 'up' ? '↗' : '↘' }}
							</div>
						</div>
					</div>

					<div v-if="inSurveyOverviewPage" class="cell clicks-cell">
						<div class="clicks-counter">
							<span class="clicks-number">{{ room.count }}</span>
							<span class="clicks-label">{{ $t('clicks') }}</span>
						</div>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

<script>
import utilService from '../../common/services/utilService'

export default {
	props: {
		roomsData: { type: Array, required: true },
		shift: { type: Object, required: false },
		inSurveyOverviewPage: { type: Boolean, default: false },
		initialViewMode: { type: String, default: 'table' },
	},
	data() {
		return {
			overviewTableHeader: ['room_name', 'arrival', 'status', 'score'],
			surveyOverviewTableHeader: ['room_name', 'clicks'],
			sortKey: null,
			sortOrder: 1,
			originalRoomsData: [],
			viewMode: this.initialViewMode || 'table',
		}
	},
	created() {
		this.originalRoomsData = [...this.roomsData]
	},
	methods: {
		/** Format arrival time in company timezone (shift.gmt). No local time fallback. */
		formatArrivalTime(isoTime) {
			const gmt = this.shift && this.shift.gmt != null && this.shift.gmt !== '' ? this.shift.gmt : null
			return utilService.formatHMInCompanyTz(isoTime, gmt)
		},
		getStatusClass(openTime, isOpen) {
			try {
				const minPassed = Math.floor((new Date().getTime() - new Date(openTime).getTime()) / (1000 * 60))
				if (!isOpen) return 'status-ok'
				else if (minPassed > this.shift.max_sla) return 'status-critical'
				else return 'status-warning'
			} catch (err) {
				return 'status-unknown'
			}
		},
		getStatusText(openTime, isOpen) {
			try {
				const minPassed = Math.floor((new Date().getTime() - new Date(openTime).getTime()) / (1000 * 60))
				if (!isOpen) return this.$t('available')
				else if (minPassed > this.shift.max_sla) return this.$t('critical')
				else return this.$t('busy')
			} catch (err) {
				return this.$t('unknown')
			}
		},
		getCardStatusClass(room) {
			if (this.inSurveyOverviewPage) return ''
			return this.getStatusClass(room.openTime, room.isOpen)
		},
		isUrgent(room) {
			if (this.inSurveyOverviewPage) return false
			try {
				const minPassed = Math.floor((new Date().getTime() - new Date(room.openTime).getTime()) / (1000 * 60))
				return room.isOpen && minPassed > this.shift.max_sla
			} catch (err) {
				return false
			}
		},
		iconToShow(openTime, isOpen) {
			let icon
			try {
				const minPassed = Math.floor((new Date().getTime() - new Date(openTime).getTime()) / (1000 * 60))
				if (!isOpen) icon = require('@/assets/real_time_icons/OK.svg')
				else if (minPassed > this.shift.max_sla) icon = require('@/assets/performance/warning.svg')
				else icon = require('@/assets/real_time_icons/warning_orange.svg')
			} catch (err) {
				icon = null
			}
			return icon
		},
		getIconSortValue(openTime, isOpen) {
			let minPassed
			try {
				minPassed = Math.floor((new Date().getTime() - new Date(openTime).getTime()) / (1000 * 60))
			} catch (err) {
				minPassed = Infinity
			}
			if (!isOpen) return 0
			if (minPassed > this.shift.max_sla) return 2
			return 1
		},
		sortTable(header) {
			if (this.sortKey === header) {
				this.sortOrder *= -1
			} else {
				this.sortKey = header
				this.sortOrder = 1
			}
			this.roomsData = [...this.originalRoomsData]
			this.sortData()
		},
		sortData() {
			const header = this.sortKey

			this.roomsData.sort((a, b) => {
				let aValue, bValue

				if (header === 'arrival') {
					aValue = a.arrival ? new Date(a.arrival.time).getTime() : 0
					bValue = b.arrival ? new Date(b.arrival.time).getTime() : 0
				} else if (header === 'status') {
					aValue = this.getIconSortValue(a.openTime, a.isOpen)
					bValue = this.getIconSortValue(b.openTime, b.isOpen)
				} else {
					if (header === 'room_name') {
						aValue = a.name.toLowerCase()
						bValue = b.name.toLowerCase()
					} else {
						aValue = typeof a[header] === 'string' ? a[header].toLowerCase() : a[header]
						bValue = typeof b[header] === 'string' ? b[header].toLowerCase() : b[header]
					}
				}

				if (aValue < bValue) return this.sortOrder
				if (aValue > bValue) return -this.sortOrder
				return 0
			})
		}
	}
}
</script>

<style lang="scss" scoped>
@use "@/styles/mixins" as *;

.overview-room-table {
	grid-column: 13 / 17;
	grid-row: 1 / 7;
	padding: 10px 5px;
	margin-bottom: 3%;
	@include shadow-border;
	background: #fafbfc;
	border-radius: 12px;
	overflow: hidden;
	display: flex;
	flex-direction: column;
}

// Controls Section
.table-controls {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 12px 16px;
	background: white;
	border-bottom: 1px solid #e1e5e9;
	border-radius: 8px 8px 0 0;
	margin-bottom: 8px;

	.view-toggle {
		display: flex;
		background: #f1f3f4;
		border-radius: 6px;
		padding: 2px;

		.toggle-btn {
			padding: 6px 12px;
			border: none;
			background: transparent;
			color: #5f6368;
			border-radius: 4px;
			cursor: pointer;
			font-size: 0.85rem;
			font-weight: 500;
			transition: all 0.2s ease;
			display: flex;
			align-items: center;
			gap: 4px;

			.icon {
				font-size: 0.9rem;
			}

			&.active {
				background: white;
				color: #1a73e8;
				box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
			}

			&:hover:not(.active) {
				background: rgba(255, 255, 255, 0.7);
			}
		}
	}

	.quick-stats {
		.stat-item {
			display: flex;
			flex-direction: column;
			align-items: center;
			gap: 2px;

			.stat-value {
				font-size: 1.25rem;
				font-weight: 700;
				color: #1a73e8;
			}

			.stat-label {
				font-size: 0.75rem;
				color: #5f6368;
				text-transform: uppercase;
				letter-spacing: 0.5px;
			}
		}
	}
}

// Cards View
.cards-container {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
	gap: 12px;
	padding: 0 8px;
	overflow-y: auto;
	height: calc(100% - 70px);

	.room-card {
		background: white;
		border-radius: 8px;
		padding: 16px;
		border: 1px solid #e1e5e9;
		transition: all 0.2s ease;
		cursor: pointer;

		&:hover {
			transform: translateY(-2px);
			box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
		}

		&.status-critical {
			border-left: 4px solid #ea4335;
		}

		&.status-warning {
			border-left: 4px solid #fbbc04;
		}

		&.status-ok {
			border-left: 4px solid #34a853;
		}

		.card-header {
			display: flex;
			justify-content: space-between;
			align-items: flex-start;
			margin-bottom: 12px;

			.room-title {
				font-size: 1rem;
				font-weight: 600;
				color: #202124;
				margin: 0;
				line-height: 1.2;
			}

			.status-indicator {
				.status-icon-small {
					width: 16px;
					height: 16px;
					margin-top: 2px;
				}
			}
		}

		.card-content {
			.metric {
				display: flex;
				justify-content: space-between;
				align-items: center;
				margin-bottom: 8px;

				.metric-label {
					font-size: 0.8rem;
					color: #5f6368;
					font-weight: 500;
				}

				.metric-value {
					font-weight: 600;
					color: #202124;
					display: flex;
					align-items: center;
					gap: 6px;

					&.large {
						font-size: 1.2rem;
						color: #1a73e8;
					}

					.arrival-badge {
						background: #e8f0fe;
						color: #1a73e8;
						padding: 2px 6px;
						border-radius: 12px;
						font-size: 0.7rem;
						font-weight: 600;
					}
				}

				.score-display {
					padding: 4px 8px;
					border-radius: 4px;
					color: white;
					font-weight: 600;
					display: flex;
					align-items: center;
					gap: 4px;
				}
			}
		}
	}
}

// Modern Table View
.modern-table {
	flex: 1;
	display: flex;
	flex-direction: column;
	overflow: hidden;

	.table-header-row {
		display: flex;
		background: white;
		border-bottom: 2px solid #e1e5e9;
		padding: 0 8px;

		.header-cell {
			flex: 1;
			padding: 12px 8px;
			cursor: pointer;
			user-select: none;

			&.sorted {
				background: #e8f0fe;
			}

			.header-content {
				display: flex;
				justify-content: space-between;
				align-items: center;

				span {
					font-weight: 600;
					color: #5f6368;
					font-size: 0.85rem;
					text-transform: uppercase;
					letter-spacing: 0.5px;
				}

				.sort-controls {
					display: flex;
					flex-direction: column;
					gap: 1px;

					.sort-btn {
						width: 16px;
						height: 10px;
						border: none;
						background: #f1f3f4;
						color: #9aa0a6;
						font-size: 0.6rem;
						cursor: pointer;
						display: flex;
						align-items: center;
						justify-content: center;
						border-radius: 2px;

						&.active {
							background: #1a73e8;
							color: white;
						}

						&.up {
							border-radius: 2px 2px 0 0;
						}

						&.down {
							border-radius: 0 0 2px 2px;
						}
					}
				}
			}
		}
	}

	.table-body-modern {
		flex: 1;
		overflow-y: auto;
		padding: 0 8px;

		.table-row-modern {
			display: flex;
			align-items: center;
			padding: 12px 0;
			border-bottom: 1px solid #f1f3f4;
			transition: all 0.2s ease;

			&:hover {
				background: #f8f9fa;
				transform: translateX(4px);
			}

			&.urgent {
				background: #fef7e0;
				border-left: 3px solid #f9ab00;
				animation: pulse 2s infinite;
			}

			.cell {
				flex: 1;
				padding: 0 8px;

				&.name-cell {
					.room-info {
						.room-name {
							font-weight: 600;
							color: #202124;
							display: block;
							font-size: 0.9rem;
						}

						.room-subtitle {
							font-size: 0.75rem;
							color: #5f6368;
							margin-top: 2px;
							display: block;
						}
					}
				}

				&.arrival-cell {
					.arrival-info {
						display: flex;
						flex-direction: column;
						align-items: center;
						gap: 4px;

						.time-display {
							font-family: 'Courier New', monospace;
							font-weight: 600;
							color: #1a73e8;
							background: #e8f0fe;
							padding: 2px 6px;
							border-radius: 4px;
							font-size: 0.8rem;
						}

						.count-pill {
							background: #34a853;
							color: white;
							padding: 2px 8px;
							border-radius: 12px;
							font-size: 0.7rem;
							font-weight: 600;
						}
					}

					.no-arrival {
						text-align: center;
						color: #9aa0a6;
						font-size: 0.8rem;
						font-style: italic;
					}
				}

				&.status-cell {
					display: flex;
					justify-content: center;

					.status-icon-container {
						display: flex;
						align-items: center;
						justify-content: center;
						padding: 4px;

						.status-icon {
							width: 20px;
							height: 20px;
							transition: all 0.2s ease;
							filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));

							&:hover {
								transform: scale(1.1);
							}
						}
					}
				}

				&.score-cell {
					display: flex;
					justify-content: center;

					.score-widget {
						display: flex;
						align-items: center;
						gap: 6px;
						padding: 6px 10px;
						border-radius: 6px;
						color: white;
						font-weight: 600;

						.score-main {
							font-size: 1.1rem;
						}

						.score-trend {
							font-size: 1rem;
							opacity: 0.9;
						}
					}
				}

				&.clicks-cell {
					display: flex;
					justify-content: center;

					.clicks-counter {
						text-align: center;

						.clicks-number {
							display: block;
							font-size: 1.3rem;
							font-weight: 700;
							color: #1a73e8;
						}

						.clicks-label {
							font-size: 0.7rem;
							color: #5f6368;
							text-transform: uppercase;
						}
					}
				}
			}
		}
	}
}

@keyframes pulse {
	0%, 100% { opacity: 1; }
	50% { opacity: 0.8; }
}
</style>